import React, { useState, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Download,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

/**
 * AbsenceImportUpload Component
 * Handles bulk import of absences from CSV/Excel files
 * Requirements: 8.1, 8.4, 8.6
 */
const AbsenceImportUpload = ({ onImportComplete, onClose }) => {
  const [step, setStep] = useState('upload'); // upload, preview, importing, complete
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      const validExtensions = ['.csv', '.xlsx'];
      
      const hasValidExtension = validExtensions.some(ext => 
        selectedFile.name.toLowerCase().endsWith(ext)
      );
      
      if (!validTypes.includes(selectedFile.type) && !hasValidExtension) {
        toast({
          title: 'Invalid File Type',
          description: 'Please upload a CSV or Excel (.xlsx) file',
          variant: 'destructive'
        });
        return;
      }
      
      setFile(selectedFile);
      validateFile(selectedFile);
    }
  };


  const validateFile = async (fileToValidate) => {
    setLoading(true);
    setStep('preview');
    
    try {
      const formData = new FormData();
      formData.append('file', fileToValidate);
      
      const response = await fetch('/api/absences/import/validate', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Validation failed');
      }
      
      const result = await response.json();
      
      if (!result.success) {
        toast({
          title: 'Validation Error',
          description: result.error,
          variant: 'destructive'
        });
        setStep('upload');
        setFile(null);
        return;
      }
      
      setValidationResult(result.validation_result);
      
      // Pre-select all valid rows
      const initialSelection = {};
      result.validation_result.rows.forEach((row, index) => {
        if (row.is_valid) {
          initialSelection[index] = true;
        }
      });
      setSelectedRows(initialSelection);
      
    } catch (error) {
      console.error('Validation failed:', error);
      toast({
        title: 'Validation Failed',
        description: error.message || 'Failed to validate import file',
        variant: 'destructive'
      });
      setStep('upload');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!validationResult) return;
    
    setLoading(true);
    setStep('importing');
    
    try {
      // Filter to only selected valid rows
      const rowsToImport = validationResult.rows.filter((row, index) => 
        selectedRows[index] && row.is_valid
      );
      
      if (rowsToImport.length === 0) {
        toast({
          title: 'No Rows Selected',
          description: 'Please select at least one valid row to import',
          variant: 'destructive'
        });
        setStep('preview');
        setLoading(false);
        return;
      }
      
      const response = await apiService.makeRequest('/api/absences/import/execute', {
        method: 'POST',
        body: JSON.stringify({
          validated_rows: rowsToImport,
          skip_invalid: true
        })
      });
      
      setImportResult(response);
      setStep('complete');
      
      toast({
        title: 'Import Complete',
        description: `Successfully imported ${response.success_count} absence records`
      });
      
      if (onImportComplete) {
        onImportComplete(response);
      }
      
    } catch (error) {
      console.error('Import failed:', error);
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to import absences',
        variant: 'destructive'
      });
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch('/api/absences/import/template', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to download template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'absence_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download template',
        variant: 'destructive'
      });
    }
  };

  const toggleRowExpanded = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleRowSelected = (index) => {
    setSelectedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const selectAllValid = () => {
    const newSelection = {};
    validationResult?.rows.forEach((row, index) => {
      if (row.is_valid) {
        newSelection[index] = true;
      }
    });
    setSelectedRows(newSelection);
  };

  const deselectAll = () => {
    setSelectedRows({});
  };

  const resetImport = () => {
    setStep('upload');
    setFile(null);
    setValidationResult(null);
    setImportResult(null);
    setExpandedRows({});
    setSelectedRows({});
  };


  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium mb-2">Upload Absence Import File</h3>
        <p className="text-gray-600 text-sm">
          Upload a CSV or Excel file containing absence records to import
        </p>
      </div>
      
      <div 
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const droppedFile = e.dataTransfer.files[0];
          if (droppedFile) {
            setFile(droppedFile);
            validateFile(droppedFile);
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx"
          onChange={handleFileSelect}
          className="hidden"
        />
        <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 mb-2">
          Drag and drop your file here, or click to browse
        </p>
        <p className="text-sm text-gray-500">
          Supported formats: CSV, Excel (.xlsx)
        </p>
      </div>
      
      <div className="flex justify-center">
        <Button variant="outline" onClick={handleDownloadTemplate}>
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </Button>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Required Columns</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>employee_id</strong> or <strong>employee_name</strong> - Employee identifier</li>
          <li>• <strong>date</strong> - Absence date (YYYY-MM-DD)</li>
          <li>• <strong>leave_type</strong> - Type of leave (PTO, SICK, etc.)</li>
          <li>• <strong>hours</strong> - Number of hours</li>
        </ul>
        <h4 className="font-medium text-blue-800 mt-3 mb-2">Optional Columns</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>end_date</strong> - End date for multi-day absences</li>
          <li>• <strong>purpose</strong> - Reason for absence</li>
          <li>• <strong>notes</strong> - Additional notes</li>
        </ul>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Validation Preview</h3>
          <p className="text-sm text-gray-600">
            {file?.name} - {validationResult?.total_rows} rows found
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Badge variant="outline" className="bg-green-50 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            {validationResult?.valid_rows} Valid
          </Badge>
          <Badge variant="outline" className="bg-red-50 text-red-700">
            <XCircle className="w-3 h-3 mr-1" />
            {validationResult?.invalid_rows} Invalid
          </Badge>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 text-sm">
        <Button variant="ghost" size="sm" onClick={selectAllValid}>
          Select All Valid
        </Button>
        <Button variant="ghost" size="sm" onClick={deselectAll}>
          Deselect All
        </Button>
        <span className="text-gray-500">
          {Object.values(selectedRows).filter(Boolean).length} selected for import
        </span>
      </div>
      
      <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left w-10"></th>
              <th className="px-3 py-2 text-left">Row</th>
              <th className="px-3 py-2 text-left">Employee</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Hours</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left w-10"></th>
            </tr>
          </thead>
          <tbody>
            {validationResult?.rows.map((row, index) => (
              <React.Fragment key={index}>
                <tr className={`border-t ${row.is_valid ? 'hover:bg-gray-50' : 'bg-red-50'}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedRows[index] || false}
                      onChange={() => toggleRowSelected(index)}
                      disabled={!row.is_valid}
                      className="rounded"
                    />
                  </td>
                  <td className="px-3 py-2">{row.row.row_number}</td>
                  <td className="px-3 py-2">
                    {row.matched_employee_name || row.row.employee_name || row.row.employee_id || '-'}
                    {row.matched_employee_email && (
                      <span className="text-gray-500 text-xs block">{row.matched_employee_email}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{row.row.date}</td>
                  <td className="px-3 py-2">{row.row.leave_type}</td>
                  <td className="px-3 py-2">{row.row.hours}</td>
                  <td className="px-3 py-2">
                    {row.is_valid ? (
                      <Badge className="bg-green-100 text-green-700">Valid</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">Invalid</Badge>
                    )}
                    {row.warnings.length > 0 && (
                      <Badge className="bg-yellow-100 text-yellow-700 ml-1">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {row.warnings.length}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {(row.errors.length > 0 || row.warnings.length > 0 || row.suggested_matches.length > 0) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRowExpanded(index)}
                      >
                        {expandedRows[index] ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </td>
                </tr>
                {expandedRows[index] && (
                  <tr className="bg-gray-50">
                    <td colSpan={8} className="px-6 py-3">
                      {row.errors.length > 0 && (
                        <div className="mb-2">
                          <span className="font-medium text-red-700">Errors:</span>
                          <ul className="list-disc list-inside text-red-600 text-sm">
                            {row.errors.map((error, i) => (
                              <li key={i}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {row.warnings.length > 0 && (
                        <div className="mb-2">
                          <span className="font-medium text-yellow-700">Warnings:</span>
                          <ul className="list-disc list-inside text-yellow-600 text-sm">
                            {row.warnings.map((warning, i) => (
                              <li key={i}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {row.suggested_matches.length > 0 && (
                        <div>
                          <span className="font-medium text-blue-700">Suggested Matches:</span>
                          <ul className="text-sm text-blue-600 mt-1">
                            {row.suggested_matches.map((match, i) => (
                              <li key={i} className="flex items-center space-x-2">
                                <span>{match.employee_name}</span>
                                <span className="text-gray-500">({match.employee_id})</span>
                                <span className="text-gray-400">
                                  {Math.round(match.similarity_score * 100)}% match
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={resetImport}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button 
          onClick={handleExecuteImport}
          disabled={Object.values(selectedRows).filter(Boolean).length === 0}
        >
          <Upload className="w-4 h-4 mr-2" />
          Import {Object.values(selectedRows).filter(Boolean).length} Records
        </Button>
      </div>
    </div>
  );


  const renderImportingStep = () => (
    <div className="text-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <h3 className="text-lg font-medium">Importing Absences...</h3>
      <p className="text-gray-600">Please wait while we process your import</p>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-medium">Import Complete</h3>
        <p className="text-gray-600">Your absence records have been imported</p>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-green-600">
              {importResult?.success_count || 0}
            </div>
            <div className="text-sm text-gray-600">Imported</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-yellow-600">
              {importResult?.skipped_count || 0}
            </div>
            <div className="text-sm text-gray-600">Skipped</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-red-600">
              {importResult?.failed_count || 0}
            </div>
            <div className="text-sm text-gray-600">Failed</div>
          </CardContent>
        </Card>
      </div>
      
      {importResult?.errors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-medium text-red-800 mb-2">Errors</h4>
          <ul className="text-sm text-red-700 space-y-1">
            {importResult.errors.map((error, index) => (
              <li key={index}>
                Row {error.row_number}: {error.errors.join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="flex justify-center space-x-4 pt-4 border-t">
        <Button variant="outline" onClick={resetImport}>
          Import More
        </Button>
        <Button onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center">
          <Upload className="w-5 h-5 mr-2" />
          Bulk Absence Import
        </CardTitle>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading && step === 'preview' ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Validating file...</p>
          </div>
        ) : (
          <>
            {step === 'upload' && renderUploadStep()}
            {step === 'preview' && renderPreviewStep()}
            {step === 'importing' && renderImportingStep()}
            {step === 'complete' && renderCompleteStep()}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AbsenceImportUpload;
